import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import path from 'path';
import Cropper from 'cropperjs';
import tooltip from '@/mixins/components/tooltip';
import { guid } from '@/mixins/guid';
import dropsLoader from '@/mixins/components/drops-loader';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import { cognitoMixins } from '@storm/mixins/cognito';

export default Vue.component('storm-my-profile', {
  template: require('./template.html'),
  name: 'storm-my-profile',
  mixins: [guid, uploader, cognitoMixins],
  computed: {
    ...mapGetters(['stormUser', 'stormUsers']),
  },
  components: {
    tooltip,
    dropsLoader,
    uploadProgress,
  },
  data() {
    return {
      name: '',
      pic: '',
      newPic: '',
      password: '',
      showPass: false,
      newpassword: '',
      showNewPass: false,
      confirmpassword: '',
      showConfirmPass: false,
      showHintGroup: false,
      confirmError: false,
      criteriaError: false,
      loaders: {
        name: false,
        pic: false,
        password: false,
      },
      edit: {
        name: false,
        pic: false,
        password: false,
      },
      hint: {
        lc: false,
        uc: false,
        num: false,
        len: false,
        sym: false,
      },
      openCropper: false,
      cropper: null,
      saveError: false,
    };
  },
  watch: {
    openCropper(open) {
      if (open) {
        setTimeout(() => {
          const image = this.$refs.imageCropper;
          this.cropper = new Cropper(image, {
            aspectRatio: 1 / 1,
            dragMode: 'move',
            autoCropArea: 1,
            zoomable: false,
          });
        }, 300);
      }
    },
  },
  created() {
    this.name = this.stormUser.name;
    this.pic = this.stormUser.pic;
    this.resetHint();
  },
  methods: {
    saveName() {
      this.$validator.validate('name', this.name).then(async (result) => {
        if (result) {
          this.loaders.name = true;
          const notif = {
            id: this.guid(),
            type: 'success',
            message: 'Name successfully updated!',
          };
          try {
            await this.cognitoUpdateAttrs({
              attrs: [
                {
                  Name: 'name',
                  Value: this.name,
                },
              ],
            });
            this.$store.dispatch('setGlobalValue', {
              item: this.stormUser,
              key: 'name',
              value: this.name,
            });
            const user = this.stormUsers.find(user => user.Username === this.stormUser.sub);
            if (user) {
              const name = user.Attributes.find(attr => attr.Name === 'name');
              if (name) {
                this.$store.dispatch('setGlobalValue', {
                  item: name,
                  key: 'Value',
                  value: this.name,
                });
              }
            }
            this.edit.name = false;
          } catch (e) {
            notif.type = 'error';
            notif.message = e.message || e;
          } finally {
            this.loaders.name = false;
            this.$store.dispatch('addNotification', notif);
          }
        }
      });
    },
    async savePassword() {
      const oldPassed = await this.$validator.validate('password', this.password);
      const newPassed = await this.$validator.validate('newpassword', this.newpassword);
      const conPassed = await this.$validator.validate('confirmpassword', this.confirmpassword);
      const criteriaError = Object.values(this.hint).filter(result => !result);
      if (oldPassed && newPassed && conPassed && !this.confirmError && criteriaError.length < 1) {
        this.loaders.password = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'Password updated!',
        };
        try {
          await this.cognitoChangePassword({
            oldPassword: this.password,
            newPassword: this.newpassword,
          });
          this.edit.password = false;
        } catch (e) {
          notif.type = 'error';
          notif.message = e.message || e;
        } finally {
          this.loaders.password = false;
          this.$store.dispatch('addNotification', notif);
        }
      }
    },
    showHint() {
      if (!this.showHintGroup) {
        this.showHintGroup = true;
      }
      const lc = /^(?=.*[a-z]).+$/;
      const uc = /^(?=.*[A-Z]).+$/; // uppercase letter
      const num = /^(?=.*\d).+$/; // a number
      const sym = /^(?=.*[!@#$%^&*_-]).+$/; // symbol
      const len = /^.{7,}.+$/; // at least 8 chars
      // check lowercase letter
      this.hint.lc = lc.test(this.newpassword);
      // check uppercase letter
      this.hint.uc = uc.test(this.newpassword);
      // check for a number
      this.hint.num = num.test(this.newpassword);
      // check for symbol
      this.hint.sym = sym.test(this.newpassword);
      // check password length
      this.hint.len = len.test(this.newpassword);
    },
    resetHint() {
      this.showHintGroup = false;
      this.hint = {
        lc: false,
        uc: false,
        num: false,
        len: false,
        sym: false,
      };
    },
    checkConfirm() {
      this.newpassword = this.newpassword.replace(/\s+/g, '');
      if (this.confirmpassword.trim() !== '') {
        this.checkPass();
      }
    },
    checkPass() {
      if (this.newpassword === this.confirmpassword) {
        this.confirmError = false;
      } else {
        this.confirmError = 'New password does not match.';
      }
    },
    async savePic() {
      this.loaders.pic = true;
      const notif = {
        id: this.guid(),
        type: 'success',
        message: 'Picture successfully updated!',
      };
      try {
        await this.cognitoUpdateAttrs({
          attrs: [
            {
              Name: 'custom:pic',
              Value: this.pic,
            },
          ],
        });
        this.$store.dispatch('setGlobalValue', {
          item: this.stormUser,
          key: 'pic',
          value: this.pic,
        });
        const user = this.stormUsers.find(user => user.Username === this.stormUser.sub);
        if (user) {
          const pic = user.Attributes.find(attr => attr.Name === 'custom:pic');
          if (pic) {
            this.$store.dispatch('setGlobalValue', {
              item: pic,
              key: 'Value',
              value: this.pic,
            });
          }
        }
        this.edit.pic = false;
      } catch (e) {
        notif.type = 'error';
        notif.message = e.message || e;
      } finally {
        this.loaders.pic = false;
        this.$store.dispatch('addNotification', notif);
      }
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    openLocalImg(e) {
      const evt = e;
      let newFile = [...evt.target.files];
      this.pic = [...evt.target.files];
      this.$validator.validate('pic', this.pic).then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.openCropper = base64File;
              // gather naming information
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = _.trim(fileName);
              fileName = _.replace(fileName, /\s+/g, '_');
              this.newPic = `storm/${this.stormUser.sub}/${fileName}`;
            } catch (err) {
              this.$log.error(err);
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    destroyCropper() {
      this.openCropper = false;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    async uploadCropped() {
      try {
        const canvas = this.cropper.getCroppedCanvas({
          width: 96,
          height: 96,
          imageSmoothingQuality: 'high',
        });
        const blob = await this.getBlob(canvas);
        // send off to our s3 upload mixin
        const pic = await this.uploadFile({
          file: this.newPic,
          body: blob,
          Bucket: process.env.S3_TICKET_ASSETS_BUCKET,
          copyToOwner: false,
          bucketPath: '',
        });
        this.pic = pic.Location;
        this.$store.dispatch('setGlobalValue', {
          item: this.stormUser,
          key: 'pic',
          value: pic.Location,
        });
        this.destroyCropper();
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
        }
      }
    },
    formatErrors(arr) {
      return arr.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/storm/pages/settings-modal/my-profile/index.js